



































































































































































































































































import { Component, Vue, Watch } from 'vue-property-decorator';
import TextField from '@/components/atoms/TextField/TextField.vue';
import TextArea from '@/components/atoms/TextArea/TextArea.vue';
import Typography from '@/components/atoms/Typography/Typography.vue';
import Title from '@/components/molecules/Typography/HeadlineXSmall.vue';
import MarketingCenterCanvas from '@/components/organisms/MarketingCenterCanvas/MarketingCenterCanvas.vue';
import FormatSelector from '@/components/organisms/MarketingCenter/FormatSelector/FormatSelector.vue';
import FilledButton from '@/components/molecules/FilledButton/FilledButton.vue';
import OutlinedButton from '@/components/molecules/OutlinedButton/OutlinedButton.vue';
import ImageUpload from '@/components/ImageUpload/ImageUpload.vue';
import ColorPallete from '@/components/atoms/ColorPallete/ColorPallete.vue';
import ModalOptions from '@/components/Modal/ModalOptions';
import eventBus from '@/utils/eventBus';
import MarketingCenterService from '@/api/MarketingCenterService';
import { updateRegistration } from '@/utils/updateRegistration';
import CollapseRow from '@/componentsV2/molecules/CollapseRow/CollapseRow.vue';
import Page from '@/componentsV2/molecules/Page/Page.vue';

import {
  TemplatesDictionary,
  EMarketingCenterTemplates,
  IMarketingCenterTemplate
} from '@/models/marketingCenter/template';
import {
  IMarketingCenterCreatePostDto,
  IMarketingCenterGetAsset,
  IMarketingCenterPost
} from '@/models/marketingCenter/post';
import { EMarketingCenterColorSchemes } from '@/models/marketingCenter/colorScheme';
import { EMarketingCenterFormats } from '@/models/marketingCenter/format';
import { BreadcrumbData } from '@/componentsV2/molecules/Breadcrumbs/Breadcrumbs.vue';

@Component({
  components: {
    Title,
    TextArea,
    TextField,
    Typography,
    FilledButton,
    OutlinedButton,
    ImageUpload,
    ColorPallete,
    MarketingCenterCanvas,
    FormatSelector,
    CollapseRow,
    Page
  }
})
export default class MarketingCenterPostEditor extends Vue {
  format = EMarketingCenterFormats.instagramPost;

  errors: string[] = [];
  marketingCenterAPI = new MarketingCenterService();

  postCreated = true;
  saveLoading = false;
  saved = true;
  postId = '';
  postName = '';
  postTitle = '';
  postTitlePlacholder = false;
  postText = '';
  postTextPlaceholder = false;
  postBrandSrc = '';
  postBrandId = '';
  postColorScheme = '' as EMarketingCenterColorSchemes;
  postTemplate = '' as EMarketingCenterTemplates;
  postAssets = [] as IMarketingCenterGetAsset[];
  collapseOpened = true;

  get isMobileDevice() {
    return this.$mobileCheck();
  }

  created() {
    if (this.shouldCreatePost) {
      this.initFromTemplate();
    } else {
      this.initFromPost();
    }
  }

  handleImageUpload(event: any) {
    this.$analytics({
      eventCategory: `${this.gaContextPage} | Change Post Image`,
      eventAction: 'change',
      eventLabel: 'Changed post image'
    });

    this.postBrandId = event.id;
  }

  handleFormatSelector(formatKey: string) {
    this.$analytics({
      eventCategory: `${this.gaContextPage} | Post preview`,
      eventAction: 'change',
      eventLabel: `Clicked on the "${formatKey.split('-').join(' ')}" button`
    });
  }

  handleCollapse() {
    this.collapseOpened = !this.collapseOpened;
  }

  initFromTemplate() {
    this.$showLoaderWithLocker();

    const setInfo = () => {
      this.postTemplate = this.$route.params
        .template as EMarketingCenterTemplates;

      this.postCreated = false;
      this.saved = false;
      this.postColorScheme =
        this.template.placeholders.colorScheme ||
        ('' as EMarketingCenterColorSchemes);

      this.postTitle = this.template.placeholders.title;
      this.postTitlePlacholder = true;
      this.postText = this.template.placeholders.text;
      this.postTextPlaceholder = true;

      this.postBrandId = this.businessImage.id;
      this.postBrandSrc = this.businessImage.src;

      this.$hideLoader();
    };

    if (this.businessImage.src) {
      setInfo();
    } else {
      eventBus.$on('setBusinessInfo', () => {
        this.$nextTick(setInfo);
      });
    }
  }

  async initFromPost() {
    this.$showLoaderWithLocker();
    this.postId = this.$route.params.post;

    try {
      const post = await this.getPost();
      this.postName = post.name;
      this.postTitle = post.title;
      this.postText = post.text;
      this.postColorScheme = post.color;
      this.postBrandId = post.brand.id;
      this.postBrandSrc = post.brand.publicPath.large;
      this.postTemplate = post.template;
      this.postAssets = post.assets;
      this.$nextTick(() => {
        this.saved = true;
      });
    } catch {
      this.$notification(
        'Post could not be loaded properly. Try again later.',
        'error'
      );

      this.$router.go(-1);
    } finally {
      this.$hideLoader();
    }
  }

  async save() {
    this.$analytics({
      eventCategory: `${this.gaContextPage} | Save button`,
      eventAction: 'click',
      eventLabel: 'Clicked on the "Save" button'
    });

    const validation = this.validate();

    if (validation.error) {
      this.$notification(validation.error, 'error');
      this.errors.push(...validation.fields);
      return;
    }

    try {
      if (this.shouldCreatePost) {
        await this.createPost();
      } else {
        await this.editPost();
      }
      await this.uploadImages();

      if (this.postCreated && this.shouldCreatePost) {
        this.$router.push(
          `/marketing-center/social-media-templates/post/${
            this.postId
          }/post-editor`
        );
      }

      this.saved = true;
      this.$notification('Post saved successfully', 'success');
    } finally {
      this.saveLoading = false;
    }
  }

  async uploadImages() {
    const { canvas }: { canvas?: any } = this.$refs;
    const data = await canvas.getFormData();

    const response = await this.marketingCenterAPI.uploadAssets(
      this.postId,
      data
    );

    if (response.status !== 201) {
      throw new Error();
    }

    this.postAssets = response.data;
  }

  async editPost() {
    const data = this.createDtoData;
    this.saveLoading = true;

    const response = await this.marketingCenterAPI.editPost(this.postId, data);

    if (response.status !== 200) {
      throw new Error();
    }
  }

  async getPost() {
    this.saveLoading = true;

    const response = await this.marketingCenterAPI.getPost(this.postId);
    if (!response.data) {
      throw new Error();
    }

    this.saveLoading = false;
    return response.data;
  }

  async createPost() {
    let data = this.createDtoData;
    this.saveLoading = true;

    if (!data.brand) {
      data.brand = this.businessImage.id;
      this.postBrandId = this.businessImage.id;
    }

    const response = await this.marketingCenterAPI.createPost(data);

    if (response.status !== 201) {
      throw new Error();
    }

    updateRegistration('marketing-center');
    this.postId = response.data.id;
    this.postCreated = true;
  }

  validate() {
    const response = {
      error: '',
      fields: ['']
    };

    const defaultMessage = String(this.$t('common.alerts.fieldsError'));

    if (!this.postName) {
      response.error = defaultMessage;
      response.fields.push('post-name');
    }

    if (!this.postTitle) {
      response.error = defaultMessage;
      response.fields.push('post-title');
    }

    if (!this.templateFormConfig.hideMessageField && !this.postText) {
      response.error = defaultMessage;
      response.fields.push('post-text');
    }

    if (!response.error && !this.postColorScheme) {
      response.error = 'Please choose a color scheme.';
      response.fields = [];
    }

    return response;
  }

  returnToStart() {
    if (this.saved) {
      this.$router.push('/marketing-center/social-media-templates');
    } else {
      this.openModal('confirm', {
        onSave: () => {
          eventBus.$emit('closeModal');
          this.save();
        }
      });
    }
  }

  clearError(field: string) {
    this.errors = this.errors.filter(eachField => eachField !== field);
  }

  @Watch('createDtoData')
  onDataChange() {
    this.saved = false;
  }

  get shouldCreatePost() {
    return this.isPostCreateRoute;
  }

  get createDtoData(): IMarketingCenterCreatePostDto {
    return {
      name: this.postName,
      title: this.postTitle,
      text: this.postText,
      brand: this.postBrandId,
      color: this.postColorScheme as EMarketingCenterColorSchemes,
      template: this.postTemplate
    };
  }

  get postData(): IMarketingCenterPost {
    return {
      title: this.postTitle || this.template.placeholders.title,
      text: this.postText || this.template.placeholders.text,
      brand: this.postBrandSrc,
      color: this.postColorScheme,
      template: this.template
    };
  }

  get CanvasRefreshState() {
    return {
      brand: this.postBrandSrc,
      color: this.postColorScheme,
      template: this.template
    };
  }

  get isPostCreateRoute() {
    return this.$route.name === 'marketingCenterPostCreate';
  }

  get breadcrumbs() {
    const breadcrumbList: BreadcrumbData[] = [
      {
        label: 'Post List',
        path: '/marketing-center/social-media-templates'
      }
    ];

    if (this.isPostCreateRoute) {
      breadcrumbList.push({
        label: 'Templates',
        path: '/marketing-center/social-media-templates/template-select'
      });
    }

    breadcrumbList.push({ label: 'Post Editor' });

    return breadcrumbList;
  }

  get titleMaxLength() {
    const maxLength = this.template.placeholders.titleMaxLength || 25;

    return {
      remaining: maxLength - this.postTitle.length,
      length: `${maxLength}`
    };
  }

  get textMaxLength() {
    const maxLength = this.template.placeholders.textMaxLength || 30;

    return {
      remaining: maxLength - this.postText.length,
      length: `${maxLength}`
    };
  }

  get templateFormConfig() {
    return this.template.form || {};
  }

  get template(): IMarketingCenterTemplate {
    if (this.postTemplate) {
      return TemplatesDictionary[this.postTemplate];
    }

    return ({
      placeholders: {},
      formats: []
    } as unknown) as IMarketingCenterTemplate;
  }

  get businessImage() {
    const { business } = this.$store.getters['business/getBusinessInfo'];

    if (business && business.logo && business.logo.publicPath.small) {
      return {
        src: business.logo.publicPath.small,
        id: business.logo.id
      };
    }

    return {};
  }

  get gaContextPage() {
    return `Social Media Assets ${
      this.shouldCreatePost ? 'creation' : 'edit'
    } page`;
  }

  openModal(type: string, data?: any) {
    const keys: { [i: string]: string } = {
      delete: 'marketingCenterDeleteModal',
      share: 'marketingCenterShareModal',
      download: 'marketingCenterDownloadModal',
      confirm: 'marketingCenterConfirmModal'
    };

    eventBus.$emit(
      'openSubModal',
      new ModalOptions(
        ``,
        ``,
        'none',
        true,
        ``,
        ``,
        ``,
        false,
        keys[type],
        false,
        true,
        true,
        true,
        true,
        true,
        true
      ),
      {
        ...data,
        gaContextPage: this.gaContextPage
      }
    );
  }

  openDeleteModal() {
    this.$analytics({
      eventCategory: `${this.gaContextPage} | Open Delete Dialog`,
      eventAction: 'click',
      eventLabel: 'Clicked to open delete dialog'
    });

    this.openModal('delete', { postId: this.postId });
  }

  openShareModal() {
    this.$analytics({
      eventCategory: `${this.gaContextPage} | Open Share Modal`,
      eventAction: 'click',
      eventLabel: 'Clicked to open share modal'
    });

    this.openModal('share', { postAssets: this.postAssets });
  }

  openDownloadModal() {
    this.$analytics({
      eventCategory: `${this.gaContextPage} | Download Post`,
      eventAction: 'click',
      eventLabel: 'Clicked to open download modal'
    });

    this.openModal('download', { template: this.template });
  }
}
